export type LicenseCareerEstimate =
  | {
      valid: true;
      format: "current" | "legacy";
      issueYear: number;
      careerYears: number;
    }
  | {
      valid: false;
    };

const LEGACY_REGION_NAMES = [
  "서울",
  "부산",
  "경기",
  "강원",
  "충북",
  "충남",
  "전북",
  "전남",
  "경북",
  "경남",
  "제주",
  "대구",
  "인천",
  "광주",
  "대전",
  "울산",
];

const normalizeLicenseNumber = (licenseNumber: string): string =>
  String(licenseNumber || "").replace(/[\s-]/g, "");

const resolveIssueYear = (issueYearLastTwoDigits: number, now: Date): number => {
  const currentYear = now.getFullYear();
  const currentCentury = Math.floor(currentYear / 100) * 100;
  const currentCenturyYear = currentCentury + issueYearLastTwoDigits;

  return currentCenturyYear <= currentYear
    ? currentCenturyYear
    : currentCenturyYear - 100;
};

/**
 * 면허번호에 포함된 발급연도로 운전경력을 연도 단위로 추정한다.
 *
 * - 현행: 숫자 12자리 (지역코드 2자리 + 발급연도 2자리 + 나머지 8자리)
 * - 과거: 한글 지역명 + 숫자 10자리 (발급연도 2자리 + 나머지 8자리)
 *
 * 지역명 없이 숫자만 10자리인 값은 현행 면허번호의 지역코드 누락과 구분할 수 없어
 * 유효하지 않은 형식으로 처리한다.
 */
export const estimateDrivingCareer = (
  licenseNumber: string,
  now: Date = new Date(),
): LicenseCareerEstimate => {
  const normalized = normalizeLicenseNumber(licenseNumber);
  let issueYearText: string | null = null;
  let format: "current" | "legacy" | null = null;

  if (/^\d{12}$/.test(normalized)) {
    issueYearText = normalized.slice(2, 4);
    format = "current";
  } else {
    const legacyRegion = LEGACY_REGION_NAMES.find((region) =>
      normalized.startsWith(region),
    );
    const legacyNumber = legacyRegion
      ? normalized.slice(legacyRegion.length)
      : "";

    if (legacyRegion && /^\d{10}$/.test(legacyNumber)) {
      issueYearText = legacyNumber.slice(0, 2);
      format = "legacy";
    }
  }

  if (!issueYearText || !format) {
    return { valid: false };
  }

  const issueYear = resolveIssueYear(Number(issueYearText), now);
  const careerYears = now.getFullYear() - issueYear;

  return {
    valid: true,
    format,
    issueYear,
    careerYears,
  };
};
